/* eslint-disable no-console */
import React from 'react';
import EmailInput from '../input/email-input';
import PhoneInput from '../input/phone-input';
import SubmitButton from '../button/submit-button';
import useWelcomeFormHook from './welcome-form-hook';
import simonDataIdentifyAndTrack from '../utils/simon-data';
import { trackEvent } from '../utils/tracking';
import { getTechnicalData } from '../utils/storefront/format-resource';
import Title from '../title/title';
import Subhead from '../subhead/subhead';
import DisclaimerCheckbox from '../input/disclaimer-checkbox-input';
import LinkWithClickHandler from '../link/link-with-click-handler';
import openLinkInNewTab from '../utils/link';

const Email = ({
  setContent,
  closeModal,
  emailContent,
  noThanksMessage,
  enableSMS,
  DisclaimerErrorMessage,
  ConsentErrorMessage,
}) => {
  const handleEmailSubmit = async (inputValue, isOptIn) => {
    const emailEventSource = emailContent.event_source;
    const eventType = emailContent.event_type;

    simonDataIdentifyAndTrack(inputValue, isOptIn, emailEventSource);

    trackEvent({
      event: eventType,
      brand: 'THINX',
      email: inputValue,
      opt_in: true,
      source: emailEventSource,
    });

    if (enableSMS) {
      setContent('sms');
    } else {
      setContent('confirmation');
    }
  };

  const {
    inputValue,
    setInputValue,
    isOptIn,
    isBlurred,
    setIsBlurred,
    invalidInputError,
    setInvalidInputError,
    submitError,
    handleOptInChange,
    handleKeyDown,
    handleFormSubmit,
  } = useWelcomeFormHook('email', handleEmailSubmit, DisclaimerErrorMessage, ConsentErrorMessage);

  return (
    <div className="h-full px-4 md:px-8">
      <div className="flex h-full flex-col justify-between pt-[75px]">
        <div className="w-full">
          <Title
            text={emailContent.welcomeheading}
            classNames="text-center"
          />
          <Subhead
            text={emailContent.subheading}
            classNames="text-center"
          />
          <div className="mt-6">
            <label
              htmlFor="newsletter-email"
              className="sr-only"
            >
              Email:
            </label>

            <EmailInput
              email={inputValue}
              setEmail={setInputValue}
              id="newsletter-email"
              ariaDescribedby="emailHelp"
              error={invalidInputError}
              setError={setInvalidInputError}
              isBlurred={isBlurred}
              setIsBlurred={setIsBlurred}
              onKeyDown={handleKeyDown}
              tabIndex={1}
              EmailPlaceholder={emailContent.email_placeholder}
              EmailErrorMessage={emailContent.email_error_message}
            />
          </div>
          {emailContent.enable_disclaimer && (
            <div className="mt-6 flex items-start">
              <label
                htmlFor="registration_disclaimer"
                className="visually-hidden"
              >
                Registration Disclaimer
              </label>
              <DisclaimerCheckbox
                submitError={submitError}
                checked={isOptIn}
                handleOptInChange={handleOptInChange}
                handleKeyDown={handleKeyDown}
                handleLinkClick={openLinkInNewTab}
                disclaimerContent={emailContent.disclaimer_content}
                tabIndex={-1}
              />
            </div>
          )}

          <div className="mt-6">
            <SubmitButton
              handleSubmit={handleFormSubmit}
              classNames="button button--primary w-full"
              error={submitError}
              label={emailContent.cta_btn_txt}
              tabIndex={-1}
            />
          </div>

          <div className="mb-3 mt-6 flex w-full items-center justify-center">
            <LinkWithClickHandler
              onClick={closeModal}
              classNames="text-14"
              ariaLabel="Close modal"
              tabIndex={-1}
            >
              {noThanksMessage}
            </LinkWithClickHandler>
          </div>
        </div>
      </div>
    </div>
  );
};

const SMS = ({ setContent, closeModal, smsContent, noThanksMessage, DisclaimerErrorMessage, ConsentErrorMessage }) => {
  const handleSmsSubmit = async (inputValue) => {
    const formattedPhoneNumber = inputValue.replace(/\D/g, '');
    const yotpoSMSListID = getTechnicalData('yotpoSMSListIdWelcome');

    // Replace with your Thinx API credentials (store securely!)
    const apiId = getTechnicalData('yotpoSMSkey');
    const apiPassword = getTechnicalData('yotpoSMSsecret');
    const apiUrl = getTechnicalData('yotpoSMSAPI');

    if (!yotpoSMSListID || !apiId || !apiPassword) return;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${btoa(`${apiId}:${apiPassword}`)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          // keep this as a string, not boolean
          trigger_double_opt_in: 'false',
          customer: {
            phone: `+1${formattedPhoneNumber}`,
          },
          channels: {
            sms: {
              marketing: {
                consent: 'subscribed',
                // ensure list_id stays as a number (not a string)
                list_id: Number(yotpoSMSListID),
                source: 'yotpo_api',
              },
            },
          },
        }),
      });

      if (!response.ok) {
        console.error('Thinx API subscription failed', await response.text());
        return;
      }

      console.log('Customer subscribed successfully via Thinx API');
      setContent('confirmation');
    } catch (error) {
      console.error('Thinx API error:', error);
    }
  };

  const {
    inputValue,
    setInputValue,
    isOptIn,
    isBlurred,
    setIsBlurred,
    invalidInputError,
    setInvalidInputError,
    submitError,
    handleOptInChange,
    handleKeyDown,
    handleFormSubmit,
  } = useWelcomeFormHook('phone number', handleSmsSubmit, DisclaimerErrorMessage, ConsentErrorMessage);

  return (
    <div className="h-full px-4 md:px-8">
      <div className="flex h-full flex-col justify-between pt-[25px]">
        <div className="w-full">
          <Title
            text={smsContent.welcomeheading}
            classNames="text-center"
          />
          <Subhead
            text={smsContent.subheading}
            classNames="text-center"
          />
          <div className="mt-6">
            <label
              htmlFor="newsletter-email"
              className="sr-only"
            >
              Phone Number:
            </label>

            <PhoneInput
              phoneNumber={inputValue}
              setPhoneNumber={setInputValue}
              id="phone"
              ariaDescribedby="smsHelp"
              error={invalidInputError}
              setError={setInvalidInputError}
              isBlurred={isBlurred}
              setIsBlurred={setIsBlurred}
              onKeyDown={handleKeyDown}
              tabIndex={1}
              PhonePlaceholder={smsContent.phone_placeholder}
              PhoneErrorMessage={smsContent.phone_error_message}
            />
          </div>

          {smsContent.enable_disclaimer && (
            <div className="mt-6 flex items-start">
              <label
                htmlFor="registration_disclaimer"
                className="visually-hidden"
              >
                Registration Disclaimer
              </label>
              <DisclaimerCheckbox
                submitError={submitError}
                checked={isOptIn}
                handleOptInChange={handleOptInChange}
                handleKeyDown={handleKeyDown}
                handleLinkClick={openLinkInNewTab}
                disclaimerContent={smsContent.disclaimer_content}
                tabIndex={-1}
              />
            </div>
          )}

          <div className="mt-6">
            <SubmitButton
              handleSubmit={handleFormSubmit}
              classNames="button button--primary w-full"
              error={submitError}
              label={smsContent.cta_btn_txt}
              tabIndex={-1}
            />
          </div>

          <div className="close-model mb-3 mt-3 flex w-full items-center justify-center">
            <LinkWithClickHandler
              onClick={closeModal}
              classNames="text-14"
              ariaLabel="Close modal"
              tabIndex={-1}
            >
              {noThanksMessage}
            </LinkWithClickHandler>
          </div>
        </div>
      </div>
    </div>
  );
};

const Confirmation = ({ closeModal, SuccessMessage, SuccessMessageText, KeepShoppingBtnTxt, KeepShoppingBtnLink }) => {
  return (
    <div className="mx-7 flex h-full flex-col items-center justify-center">
      <Title
        text={SuccessMessage}
        classNames="text-center"
      />
      <Subhead
        text={SuccessMessageText}
        classNames="text-center"
      />
      {KeepShoppingBtnLink ? (
        <a
          href={KeepShoppingBtnLink}
          className="button button--primary mt-6 w-full text-center"
        >
          {KeepShoppingBtnTxt}
        </a>
      ) : (
        <button
          className="button button--primary mt-6 w-full"
          onClick={closeModal}
        >
          {KeepShoppingBtnTxt}
        </button>
      )}
    </div>
  );
};

const WelcomeOfferContent = ({
  enableSMS,
  content,
  setContent,
  closeModal,
  emailContent,
  smsContent,
  noThanksMessage,
  SuccessMessage,
  SuccessMessageText,
  KeepShoppingBtnTxt,
  KeepShoppingBtnLink,
  DisclaimerErrorMessage,
}) => {
  return (
    <>
      {content === 'email' && (
        <Email
          setContent={setContent}
          closeModal={closeModal}
          emailContent={emailContent}
          noThanksMessage={noThanksMessage}
          enableSMS={enableSMS}
          DisclaimerErrorMessage={DisclaimerErrorMessage}
        />
      )}
      {content === 'sms' && (
        <SMS
          setContent={setContent}
          closeModal={closeModal}
          smsContent={smsContent}
          noThanksMessage={noThanksMessage}
          DisclaimerErrorMessage={DisclaimerErrorMessage}
        />
      )}
      {content === 'confirmation' && (
        <Confirmation
          closeModal={closeModal}
          SuccessMessage={SuccessMessage}
          SuccessMessageText={SuccessMessageText}
          KeepShoppingBtnTxt={KeepShoppingBtnTxt}
          KeepShoppingBtnLink={KeepShoppingBtnLink}
        />
      )}
    </>
  );
};

export default WelcomeOfferContent;
